import moment from "moment";

const DAY = "YYYY-MM-DD";

export interface DateTile {
  /** Big day number (week view: the week's first day). */
  num: number;
  /** "Wednesday" (day / card) or "Week of Oct 4" (week). */
  top: string;
  /** "October 2026" (rendered uppercase). */
  month: string;
  /** "Today" / "Tomorrow" / "Yesterday" / "This week", else null. */
  rel: string | null;
}

interface DateTileInput {
  view: "day" | "week" | "card" | "list";
  /** Viewed date (browser-local Date carrying the viewed day). */
  date: Date | undefined;
  /** Week view: the first viewed day ("YYYY-MM-DD", CalendarContext dateRange.start). */
  weekStart?: string;
  /** Today in the salon's timezone ("YYYY-MM-DD"). */
  today: string;
}

/**
 * The toolbar's date tile text (design handoff header): pure, so the
 * relative-day pill and week label are tested without rendering.
 */
export function dateTileModel({
  view,
  date,
  weekStart,
  today,
}: DateTileInput): DateTile {
  const viewed = moment(date);
  if (view === "week") {
    const start = weekStart
      ? moment(weekStart, DAY)
      : viewed.clone().startOf("week");
    const end = start.clone().add(6, "days").format(DAY);
    const inWeek = today >= start.format(DAY) && today <= end;
    return {
      num: start.date(),
      top: `Week of ${start.format("MMM D")}`,
      month: start.format("MMMM YYYY"),
      rel: inWeek ? "This week" : null,
    };
  }
  const diff = viewed
    .clone()
    .startOf("day")
    .diff(moment(today, DAY).startOf("day"), "days");
  const rel =
    diff === 0
      ? "Today"
      : diff === 1
        ? "Tomorrow"
        : diff === -1
          ? "Yesterday"
          : null;
  return {
    num: viewed.date(),
    top: viewed.format("dddd"),
    month: viewed.format("MMMM YYYY"),
    rel,
  };
}
